import { useEffect, useRef, useState } from 'react';
import { ECRIRE_UNE_NOTE, NOTES } from '../partage/vocabulaire';
import './ChampsNotes.css';

/**
 * Ce qu'on écrit dans la note d'une bulle : un brouillon qui s'enregistre après une seconde de
 * silence, et en quittant le champ — **on écrit, on ne sauvegarde pas.** Le même aux notes de
 * la fiche Classique (`ChampsNotes`) et à la feuille de la fiche Note (`NoteDeLaFiche`).
 */
export function useNoteEcrite(valeur: string | null, onChanger: (valeur: string | null) => void) {
  const [brouillon, setBrouillon] = useState(valeur ?? '');
  const minuteur = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => setBrouillon(valeur ?? ''), [valeur]);
  useEffect(
    () => () => {
      if (minuteur.current) clearTimeout(minuteur.current);
    },
    []
  );

  return {
    brouillon,
    ecrire(texte: string) {
      setBrouillon(texte);
      if (minuteur.current) clearTimeout(minuteur.current);
      minuteur.current = setTimeout(() => onChanger(texte.trim() || null), 900);
    },
    quitter() {
      if (minuteur.current) clearTimeout(minuteur.current);
      if (brouillon.trim() !== (valeur ?? '')) onChanger(brouillon.trim() || null);
    },
  };
}

/**
 * Les notes — le seul champ de la fiche qui n'alimente aucun calcul, et le seul qu'on relit.
 *
 * C'est là que vit ce qu'aucun champ ne porte, notamment *pourquoi* une bulle est à ce
 * rang-là : on n'invente pas de champ « raison », elle a déjà le sien (SPEC §5.3).
 *
 * Comme les maquettes de la fiche (chantier `revue-design`, ticket 02) : écrites, elles se
 * lisent dans un cadre, et un clic les rouvre en champ ; vides, le champ est là, « Écrire une
 * note… ». Ça s'enregistre après une seconde de silence (`useNoteEcrite`).
 */
export function ChampsNotes({
  valeur,
  onChanger,
}: {
  valeur: string | null;
  onChanger: (valeur: string | null) => void;
}) {
  const [ecrit, setEcrit] = useState(false);
  const note = useNoteEcrite(valeur, onChanger);

  return (
    <section className="notes">
      <label className="fiche-intertitre" htmlFor="fiche-notes" onClick={() => setEcrit(true)}>
        {NOTES}
      </label>

      {ecrit || !valeur ? (
        <textarea
          id="fiche-notes"
          className="champs-notes"
          data-tuto="champ-notes"
          value={note.brouillon}
          rows={3}
          autoFocus={ecrit}
          placeholder={ECRIRE_UNE_NOTE}
          // Le champ vide est là dès l'ouverture : y entrer, c'est écrire. Sans ceci, la
          // première sauvegarde le changerait en texte lu sous les doigts.
          onFocus={() => setEcrit(true)}
          onChange={(e) => note.ecrire(e.target.value)}
          onBlur={() => {
            note.quitter();
            setEcrit(false);
          }}
        />
      ) : (
        <button type="button" className="champs-notes-lues" onClick={() => setEcrit(true)}>
          {valeur}
        </button>
      )}
    </section>
  );
}
